import React from 'react'
// libs
import { Field } from 'redux-form'
import { useRouter } from 'hooks/useRouter'
// icons
import AddCircle from '@mui/icons-material/AddCircle'
import { AsteriskIcon } from 'components/FormElements/components/InputLabel'
// context
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// components
import { PopoverModal } from 'components/UI'
import AttachmentsMenu from 'components/Modals/PlanItems/components/ActivitiesList/components/AddAttachmentsMenu'
// hooks
import { useCurrentForm } from 'hooks/risks'
import { useNetworkStatus } from 'hooks/useNetworkStatus'
import { useImageUpload, Image } from 'hooks/useImageUpload'
import { useAttachmentField } from '../useAttachmentField'
// types
import { Forms } from 'constants/index'
import { AttachmentType } from '../types'
import { WrappedFieldProps } from 'redux-form/lib/Field'
import { AssetType, FieldType, IField } from 'types/graphql'
// themes
import colors from 'themes/colors'
// views
import { CloseIcon, DocOrLinkWrapper, LabelContainer, Link, LinkWrapper } from './views'
// utils
import { normalizeLink, ALLOWED_MIME_TYPES } from 'utils/index'

export { AttachmentType } from '../types'

const PAPER_PROPS = { style: { width: 160 } }

interface Props extends WrappedFieldProps {
  form?: string
  field: IField
  required: boolean
  isDisabled?: boolean
}

const DocumentOrLink: React.FC<Props> = ({ field, required, input, isDisabled, form = Forms.RISK_TEMPLATE_FORM }) => {
  const router = useRouter()
  const onLine = useNetworkStatus()

  const inputFileRef = React.useRef<HTMLInputElement>(null)

  const { fieldLink } = useCurrentForm()
  const { isRisksRegister, riskTemplate } = React.useContext(RiskTemplateAPIContext)

  const { attachment, setValue, clearField, showAddLinkModal } = useAttachmentField({ input, form })

  const { onFileUpload } = useImageUpload({
    onUpload: (uploadedImage: Image | any) => setValue({ ...uploadedImage, type: AttachmentType.Image }),
    assetType: AssetType.ActivityFile,
    entityPrefix: `entity/${field.id}/assets`
  })

  const parentField = riskTemplate?.fields?.find((item) => item.id === field.parentFieldId)
  const isRegisters = isRisksRegister || router.query.multiple
  const showLabel = !isRegisters && (field.parentFieldId ? parentField?.fieldType !== FieldType.Table : true)
  const canAddAttachment = !isRegisters && !attachment
  const isForm = !!fieldLink

  return (
    <DocOrLinkWrapper $disabled={isDisabled} $isForm={isForm}>
      {showLabel && (
        <LabelContainer>
          {required && <AsteriskIcon />} {field.label}
        </LabelContainer>
      )}

      {attachment && (
        <LinkWrapper>
          {attachment._pending ? (
            <span>{attachment.name}</span>
          ) : onLine && attachment.url ? (
            <Link
              target='_blank'
              isDisabled={isDisabled}
              href={attachment.type === AttachmentType.Image ? attachment.url : normalizeLink(attachment.url)}>
              {attachment.name || attachment.title || attachment.url}
            </Link>
          ) : (
            <span>The file will be uploaded as soon as the network appears</span>
          )}
          {!isDisabled && <CloseIcon onClick={clearField} />}
        </LinkWrapper>
      )}

      {canAddAttachment && (
        <>
          <PopoverModal
            PaperProps={PAPER_PROPS}
            trigger={<AddCircle htmlColor={isDisabled ? colors.gray230 : colors.blue500} />}>
            {({ open, closePopup }) => {
              if (open && isDisabled) closePopup()
              return (
                <AttachmentsMenu
                  closePopup={closePopup}
                  addLink={showAddLinkModal}
                  addAttachment={() => inputFileRef.current?.click()}
                />
              )
            }}
          </PopoverModal>
          <input
            id='file'
            type='file'
            ref={inputFileRef}
            style={{ display: 'none' }}
            onChange={onFileUpload}
            accept={ALLOWED_MIME_TYPES.join(',')}
          />
        </>
      )}
    </DocOrLinkWrapper>
  )
}

const FieldComponent = (props) => <Field {...props} component={DocumentOrLink} />

export default FieldComponent
